import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AlertasService } from 'src/app/nucleo/utilidades/alertas.service';
import {
  CONFIGURACION_FICHA_EVEDISA_22,
  CONFIGURACION_TABLA_EVEDISA_22,
  ENCABEZADOS_EXCEL_EVEDISA_22,
} from './utilidades/evedisa-22.static';
import {
  DatosTabla,
  FilaTabla,
} from 'src/app/compartido/tabla/utilidades/tabla.models';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  capitalizar,
  formatearFecha,
  stringVacío,
  valorNumérico,
} from 'src/app/compartido/utilidades/valores.utils';
import { OrdenesService } from 'src/app/ordenes/utilidades/ordenes.service';
import {
  ItemOrdenCrearDTO,
  OrdenCrearDTO,
} from 'src/app/ordenes/utilidades/ordenes.models';
import { ImprimirEtiquetasComponent } from 'src/app/ordenes/paginas/imprimir-etiquetas/imprimir-etiquetas.component';
import {
  ClienteCrearDTO,
  DireccionEditarDTO,
} from 'src/app/clientes/utilidades/clientes.models';

@Component({
  templateUrl: './evedisa-22.component.html',
  styleUrls: ['./evedisa-22.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class Evedisa22Component implements OnInit {
  /* Configuración de la ficha */
  protected configFicha = CONFIGURACION_FICHA_EVEDISA_22;

  /* Configuración de la tabla */
  protected readonly configTabla = CONFIGURACION_TABLA_EVEDISA_22;

  /* Filas de la tabla */
  protected filas: DatosTabla = [];

  /* Referencia del componente para imprimir */
  @ViewChild('imprimir') imprimirRef!: ImprimirEtiquetasComponent;

  constructor(
    private readonly _alertasService: AlertasService,
    private readonly _ordenesService: OrdenesService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
  ) {}

  ngOnInit(): void {
    // Agregar query params a la URL si no existen
    firstValueFrom(this._route.queryParams).then((params: Params) => {
      const orgId = 15;
      const fecha = formatearFecha(new Date());

      if (params['organizacion'] !== orgId || params['fecha'] !== fecha) {
        this._router.navigate([], {
          queryParams: {
            organizacion: orgId,
            fecha: fecha,
          },
        });
      }
    });
  }

  /**
   * Pega el contenido del portapapeles en la tabla.
   * @param {ClipboardEvent} event - Evento de pegado del portapapeles.
   * @author Juan Corral
   */
  protected pegarFila(event: ClipboardEvent): void {
    // Obtener el texto del portapapeles
    const texto = event.clipboardData?.getData('text');
    event.preventDefault();
    if (!texto) return;

    // Dividir el texto en líneas
    const lineas = texto.split('\n').filter((line) => line.trim());
    if (lineas.length === 0) return;

    // Verificar encabezados
    const numColumnas = ENCABEZADOS_EXCEL_EVEDISA_22.length;
    if (lineas.some((linea) => linea.split('\t').length !== numColumnas)) {
      this._alertasService.alertarError(
        'El número de columnas no coincide con los encabezados esperados.',
      );
      return;
    }

    // Convertir las líneas en filas de la tabla
    const filas: DatosTabla = lineas.map((linea) => {
      const fila: Record<string, any> = {};
      const celdas = linea.split('\t');
      for (let i = 0; i < numColumnas; i++)
        fila[ENCABEZADOS_EXCEL_EVEDISA_22[i]] = celdas[i];
      return fila;
    });

    this.filas = [...this.filas, ...filas];
    this._cdr.markForCheck();

    this._alertasService.alertarExito(`Filas pegadas: ${filas.length}.`);
  }

  /**
   * Elimina una fila de la tabla.
   * @param {FilaTabla} fila - Fila a eliminar.
   * @author Juan Corral
   */
  protected eliminarFila(fila: FilaTabla): void {
    this.filas = this.filas.filter((f) => f !== fila);
    this._cdr.markForCheck();
  }

  /**
   * Crea las órdenes a partir de las filas de la tabla.
   * @author Juan Corral
   */
  protected async crearOrdenes$(): Promise<void> {
    const ordenes: OrdenCrearDTO[] = [];

    for (const fila of this.filas) {
      // Determinar teléfono
      let telefono = undefined;
      let celular = undefined;
      let telStr = valorNumérico(fila['Celular']?.split('//')[0])?.toString();
      if (telStr && !stringVacío(telStr)) {
        if (telStr.length == 7) telefono = valorNumérico(telStr);
        else {
          if (telStr.startsWith('57')) telStr = telStr.substring(2);
          if (telStr.length === 10) celular = valorNumérico(telStr);
        }
      }

      // Crear el cliente
      const cliente: ClienteCrearDTO = {
        tipo_identificacion: undefined,
        identificacion: fila['Nombre'],
        nombre: fila['Nombre'],
        telefono,
        celular,
        correo: undefined,
        organizacion: 15,
      };

      // Crear la dirección
      const ciudad = capitalizar(fila['Ciudad']);
      const departamento = capitalizar(fila['Departamento']);
      const direccion: DireccionEditarDTO = {
        direccion: fila['Dirección'],
        direccion_formateada: undefined,
        detalles: undefined,
        barrio: undefined,
        localidad: undefined,
        ciudad: stringVacío(ciudad) ? undefined : ciudad,
        departamento: stringVacío(departamento) ? undefined : departamento,
        latitud: undefined,
        longitud: undefined,
        creacion_manual: false,
      };

      // Crear items
      const items: ItemOrdenCrearDTO[] = [];
      if (!stringVacío(fila['Medicamento']))
        items.push({
          producto: {
            nombre: fila['Medicamento'],
            id_externo: undefined,
          },
          cantidad: valorNumérico(fila['Cantidad']) || 1,
        });

      // Crear la orden
      const orden: OrdenCrearDTO = {
        enumeracion: fila['SSC'],
        referencia: fila['SSC'],
        tipo: undefined,
        prioridad: undefined,
        origen: 26,
        centro_costo: 'DP21',
        servicio: 'Pendiente',
        recaudo: valorNumérico(fila['Recaudo']),
        direccion,
        cliente,
        items,
        formulas: [],
        notas: fila['Tipo Domicilio'] + '- Teléfonos: ' + fila['Teléfono'],
      };

      ordenes.push(orden);
    }

    for (let i = 0; i < ordenes.length; i++) {
      const orden = ordenes[i];
      try {
        const res = await this._ordenesService.crearOrden$(orden);
        this.filas = this.filas.filter(
          (fila) => fila['SSC'] !== res.referencia,
        );
      } catch (error) {
        this._alertasService.alertarError(
          `Creadas: ${i} / ${ordenes.length}.\n` +
            `Error al crear ${orden.referencia}: ${error}`,
        );
        this.imprimirRef.recargarDatos();
        this._cdr.markForCheck();
        return;
      }
    }
    this._alertasService.alertarExito(
      `Órdenes creadas exitosamente: ${ordenes.length}`,
    );
    this.imprimirRef.recargarDatos();
    this._cdr.markForCheck();
  }
}
